// The shared Shot Map / Pass Map shell — ported ONCE from js/renderers-shotmap.js's
// renderEventModule, which both legacy renderers call with a small cfg. Keeping one shell
// (not two copies) is what keeps the two modules identical by construction — the reason
// the legacy shares it.
//
// What the shell owns: the player fallback, comp/season resolve + write-back, the strip
// (comp/season + the Chart/Table toggle), the fixed stats bar, the empty states, the
// filter chips (`_eventFilters`, shared by both modules), the area narrowing and both
// Reset view buttons, the Colour-by chip. Each module supplies its stats entries, its
// pitch and its table.
//
// Reads `_`-state every render (no rebuildOnRefresh needed), so a restore — Undo, a preset,
// the header "All" — renders what was saved.

import { formatMatchDate } from "../engines/event-map-kit.js";
import { XgStory } from "./XgStory.js";
import { xgStory } from "./shotmap-view.js";
import { focusEmptyText, setModuleFocus } from "../live/event-focus.js";
import { undoApi } from "../canvas/undo.js";
import { inFocus, moduleFocus, zoneOf, type EventFocus } from "../live/event-focus.js";
import { releasePasses, selectPasses } from "../engines/event-link.js";
import { PASS_SUMMARY_LABEL, inSummaryRow, type PassSummaryKey } from "./passmap-view.js";
import { FocusChip } from "./FocusChip.js";
import { LinkedChip } from "./LinkedChip.js";
import { useLinkedSelection } from "./useLinkedSelection.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { eventChipsOf, explainPassMap, explainShotMap } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useEffect, useMemo, useReducer, useState, type ComponentType, type ReactNode } from "react";
import type { ProtoEvent, ProtoShotPassDetail } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { EventSubControls } from "./EventSubControls.js";
import { EventStatsBar } from "./EventStatsBar.js";
import { EventFilterBar } from "./EventFilterBar.js";
import { noDataMessage } from "./no-data.js";
import {
  eventInArea,
  matchesEventFilters,
  pruneStaleEventFilters,
  resolveColorMode,
  resolveEventState,
  type EventActive,
  type EventArea,
  type EventColorMode,
  type EventContext,
  type EventFilterDef,
  type ResolvedEventState,
} from "../engines/event-map-kit.js";
import { readSnapshot, snapshotPlayersOrNone } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { useEventContext } from "../hooks/useEventContext.js";
import { useShotPassEvents } from "../hooks/useShotPassEvents.js";
import { useLiveEventMap } from "../hooks/useLiveEventMap.js";
import { EVENT_KINDS } from "../live/event-map.js";
import { LIVE } from "../live/flag.js";
import { pickedShortName } from "../live/picked-players.js";

const EMPTY_EVENTS: ProtoEvent[] = [];

// Gradient ids are document-global and several Shot/Pass Maps can be open at once, so each
// pitch takes its own prefix from ONE counter (the legacy's shared _gradientSeq).
let gradientSeq = 0;
export function useGradientPrefix(): string {
  const [prefix] = useState(() => `event-grad-${++gradientSeq}`);
  return prefix;
}

export interface EventPitchProps {
  mod: HTMLElement;
  ctx: EventContext;
  /** The events after the chips AND the area. */
  events: ProtoEvent[];
  colorMode: EventColorMode;
  area: EventArea | null;
  /** Write a new zoom (the drag) or clear it (null — the corner Reset). */
  onArea: (area: EventArea | null) => void;
  /** Live Pass Map: the zone grid, when it's switched on (else null). */
  zones?: PitchZones | null;
  /** Live Pass Map: another passing module's picked passes (the shared selection) — only these stay
   *  bright and hoverable; null = none. */
  linkedIds?: ReadonlySet<string> | null;
  /** Live Pass Map: re-announce this map's own pick after a Summary row click. */
  onOwnPick?: () => void;
  /** A team / match map: its players row, above the side panel (it stays when the panel comes out). */
  below?: ReactNode;
  /** A team's whole season with no players picked (Ben, 2026-10-30: "ticking players or a zone
   *  thins the passes back to readable"): with zones on, only the picked zones' passes are drawn
   *  (none until one is picked), not ~19,000 lines under the grid. */
  zonesOnly?: boolean;
}

/** The Pass Map's zones (Ben, 2026-10-19): a 6 × 6 grid over the pitch; clicked zones keep only
 *  the passes starting there highlighted, and drag out onto a Heat Map. */
export interface PitchZones {
  /** The picked zones (`zoneOf` cells); empty = none, every pass as normal. */
  selected: number[];
  /** A click on a zone: alone → only it (again → none); `add` (Shift) → added / taken out. */
  onPick: (zone: number, add: boolean) => void;
  /** The picked zones as a focus to drag onto a Heat Map. */
  focus: EventFocus | null;
}

export interface EventModuleConfig {
  key: "shots" | "passes";
  /** Reads real data on ?live=1 (Shot Map from T2-6, Pass Map from T2-7); false = the snapshot everywhere. */
  live: boolean;
  emptyNoun: string;
  pluralNoun: string;
  filters: (ctx: EventContext) => EventFilterDef[];
  colorModes: readonly EventColorMode[];
  colorStateKey: "_shotColorBy" | "_passColorBy";
  defaultColor: string;
  /** The `.event-stat` entries (the shell owns the bar itself). */
  stats: (filtered: ProtoEvent[], events: ProtoEvent[]) => ReactNode;
  Pitch: ComponentType<EventPitchProps>;
  Table: ComponentType<{ ctx: EventContext; events: ProtoEvent[] }>;
}

/** What the shell shows, from either source: her events tree (null = none at all), the
 *  competition + season it resolved to (written back to the module), and the match context
 *  the Opposition / Game State chips read. */
/** A map showing a team or a match (teams and matches, Ben 2026-10-30): what the team view adds to
 *  the body. Absent = a player's map, as ever. */
export interface TeamMapExtras {
  /** Its switch (For | Against, or Arsenal | Sunderland on a match) and match chip, in the chips row. */
  controls: ReactNode;
  /** A match it's narrowed to, else the season. */
  matchId: number | null;
  /** A point on the xG story picked (null = the season again). */
  onPickMatch: (matchId: number | null) => void;
  /** Players unticked in the list: their events leave the pitch, the numbers and the table. */
  hiddenShooters: ReadonlySet<number>;
  /** The player chip ("Declan Rice ✕"): only their events, else everyone's. */
  onlyPlayer: number | null;
  /** A shooter hovered in the list: their shots stand out. */
  hoverIds: ReadonlySet<string> | null;
  /** The chip for the players picked ("Rice, Saka ✕"), given the events the list shows; null when
   *  everyone is. */
  playersChip: (events: readonly ProtoEvent[]) => ReactNode;
  /** The players row (Shooters / Passers) above the side panel. Given the events it lists (before
   *  its own ticks). */
  side: (shots: readonly ProtoEvent[]) => ReactNode;
}

export interface EventMapData {
  spd: ProtoShotPassDetail | null;
  state: ResolvedEventState | null;
  ctx: EventContext;
}

/** Hookless dispatch, so each source keeps its own hook order. Live (?live=1): any player in the
 *  DB, by id. Snapshot: an unknown name → the first player (the legacy's fallback), so the body's
 *  hooks always see a real player. Every event map is live now (Shot Map T2-6, Pass Map T2-7), so
 *  the switch is LIVE alone — which lets the clean build drop the snapshot path (step 1b). */
export function EventMapModule({ cfg, ...props }: ProtoModuleProps & { cfg: EventModuleConfig }) {
  if (LIVE) {
    if (!cfg.live) throw new Error(`Event map "${cfg.key}" has no live version`);
    return <LiveEventMap {...props} cfg={cfg} />;
  }
  const players = readSnapshot().players;
  const name = props.playerName && players[props.playerName] ? props.playerName : Object.keys(players)[0];
  const p = name ? players[name] : undefined;
  if (!p || !name) return <div className="stats-empty">No data.</div>;
  return <SnapshotEventMap {...props} cfg={cfg} eventsId={Number(p.id)} shortName={getPlayerShortName(p)} emptySeason={p.currentSeason ?? null} />;
}

/** `eventsId` = the shown player's id (her events are keyed by it), after the fallback. */
function SnapshotEventMap(props: ProtoModuleProps & { cfg: EventModuleConfig; eventsId: number; shortName: string; emptySeason: string | null }) {
  const { mod } = props;
  const refresh = useRefresh();
  const ctx = useEventContext();
  const spd = useShotPassEvents(props.eventsId);
  // Resolve comp/season against THIS player's data, then write the resolved ids back —
  // the legacy resolveEventState did that write inside itself.
  const state = resolveEventState(
    {
      compId: getModState<string>(mod, "_eventMapCompetitionId"),
      seasonCode: getModState<string>(mod, "_eventMapSeason"),
    },
    spd,
  );
  if (state) {
    setModState(mod, "_eventMapCompetitionId", state.compId);
    setModState(mod, "_eventMapSeason", state.seasonCode);
  }
  return <EventMapBody {...props} data={{ spd, state, ctx }} refresh={refresh} />;
}

/** The re-render the body asks for after writing `_`-state. It must re-render the PARENT: the
 *  parent resolves the competition + season from that state (and, live, asks for its data), so
 *  a body-only re-render would keep showing the old ones. */
export function useRefresh(): () => void {
  const [, refresh] = useReducer((x: number) => x + 1, 0);
  return refresh;
}

function LiveEventMap(props: ProtoModuleProps & { cfg: EventModuleConfig }) {
  const refresh = useRefresh();
  const data = useLiveEventMap(props.mod, props.playerId, EVENT_KINDS[props.cfg.key]);
  return <EventMapBody {...props} shortName={liveShortName(props)} data={data} refresh={refresh} />;
}

/** A live widget's player name: the snapshot's when she's in it, else her live pick's. */
export function liveShortName({ playerName, playerId }: Pick<ProtoModuleProps, "playerName" | "playerId">): string {
  return playerName ? getPlayerShortName(snapshotPlayersOrNone()[playerName]) : pickedShortName(playerId);
}

export function EventMapBody({
  mod,
  body,
  playerId,
  cfg,
  shortName,
  emptySeason,
  data: { spd, state, ctx },
  refresh: forceRender,
  team,
}: ProtoModuleProps & { cfg: EventModuleConfig; shortName: string; data: EventMapData; refresh: () => void; team?: TeamMapExtras;
  /** The season the "no data" message names when she has no events at all (snapshot: her
   *  current one). Live: unknown → the message stops after her name. */
  emptySeason?: string | null }) {
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = () => setFilterVersion((v) => v + 1);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const { filters } = cfg;
  const defs = useMemo(() => filters(ctx), [filters, ctx]);
  const seasonEvents = state ? state.season[cfg.key] : EMPTY_EVENTS;
  // Live: a match dragged in from Matches narrows the map to that game (Ben, 2026-10-18; event-focus.ts).
  // A team's map has no player focus: its match (if any) narrows it instead.
  const focus = LIVE && cfg.live && !team ? moduleFocus(mod, playerId) : null;
  const teamMatch = team?.matchId ?? null;
  const events = useMemo(
    () => (focus ? seasonEvents.filter((e) => inFocus(e, focus)) : teamMatch !== null ? seasonEvents.filter((e) => e.match_id === teamMatch) : seasonEvents),
    [seasonEvents, focus, teamMatch],
  );
  const focusChip = focus ? <FocusChip mod={mod} focus={focus} /> : null;
  // In the chips' row (Ben, 2026-10-20), not on a row of its own.
  const focusChipInline = focus ? <FocusChip mod={mod} focus={focus} inline /> : null;

  // Seeded only once there's data (the legacy seeds after its no-data bail-out).
  if (state && !getModState<EventActive>(mod, "_eventFilters")) setModState(mod, "_eventFilters", {});
  const active = getModState<EventActive>(mod, "_eventFilters");
  // Self-heal the chips against the whole season's events (not one match's: that would drop
  // chips the user set), in place, every render (as the legacy).
  if (state && active) pruneStaleEventFilters(active, seasonEvents, defs);

  const area = getModState<EventArea | null>(mod, "_eventAreaSelection") ?? null;
  const isTable = getModState<string>(mod, "_eventMapView") === "table";

  // The area is a second narrowing on top of the chips — the table too, not just the pitch.
  const hidden = team?.hiddenShooters;
  const only = team?.onlyPlayer ?? null;
  const filtered = useMemo(
    () =>
      active
        ? events
            .filter((e) => matchesEventFilters(e, active, defs))
            .filter((e) => eventInArea(e, area))
            .filter((e) => !hidden?.size || !e.player || !hidden.has(e.player.id))
            .filter((e) => only === null || e.player?.id === only)
        : EMPTY_EVENTS,
    // filterVersion stands in for the in-place edits to `active`.
    [events, active, defs, area, filterVersion, hidden, only],
  );

  // Live Pass Map: the zone grid (`_passZones`) and the picked zones (`_passZoneSelection`). The
  // picked zones narrow the numbers and the table; the pitch keeps every pass, the others faded.
  // A team's season opens on zones (Ben, 2026-10-30: "zones first for a team, as Catalyst"; ~19,000
  // passes are a solid blob as lines); a player on lines. The switch's own choice wins either way.
  const teamSeason = !!team && team.matchId === null;
  const zonesOn = LIVE && cfg.live && cfg.key === "passes" && (getModState<boolean>(mod, "_passZones") ?? teamSeason) === true;
  const zoneSel = zonesOn ? (getModState<number[]>(mod, "_passZoneSelection") ?? []) : [];
  // The shared selection (Ben, 2026-10-21): the other passing modules' picks narrow the numbers and the
  // table and fade the other lines (a blue chip each); they add to this map's own zones and Summary row
  // (2026-10-26), which go only when this map's pick is ✕'d elsewhere.
  const isPassMap = LIVE && cfg.live && cfg.key === "passes";
  const linkedRaw = useLinkedSelection(mod, isPassMap ? events : EMPTY_EVENTS, () => {
    if (!isPassMap) return;
    const zonesPicked = (getModState<number[]>(mod, "_passZoneSelection") ?? []).length > 0;
    if (!zonesPicked && getModState<string>(mod, "_passSummaryPick") === undefined) return;
    setModState(mod, "_passZoneSelection", []);
    setModState(mod, "_passSummaryPick", undefined);
    forceRender();
  });
  const linked = isPassMap ? linkedRaw : null;
  const linkedChip = linked ? <LinkedChip linked={linked} /> : null;
  const zoned = useMemo(
    () =>
      filtered.filter((e) => (zoneSel.length === 0 || zoneSel.includes(zoneOf(e.x, e.y))) && (linked === null || linked.ids.has(e.id))),
    [filtered, zoneSel.join(","), linked],
  );
  // This map's pick for the shared selection (Ben, 2026-10-21): its picked zones, Summary row and / or
  // filter chips ("Recipient: Ben White"). Announced when the user changes one of them — never on a
  // redraw. Its own passes only: the others' picks are layered on by whoever shows them.
  const announce = () => {
    if (!isPassMap || !active) return;
    const zonesNow = getModState<boolean>(mod, "_passZones") === true ? (getModState<number[]>(mod, "_passZoneSelection") ?? []) : [];
    const row = getModState<PassSummaryKey>(mod, "_passSummaryPick") ?? null;
    const chips = eventChipsOf(active, defs).map((c) => `${c.label}: ${c.value}`);
    if (zonesNow.length === 0 && row === null && chips.length === 0) {
      releasePasses(mod);
      return;
    }
    const ids = events
      .filter(
        (e) =>
          matchesEventFilters(e, active, defs) &&
          (zonesNow.length === 0 || zonesNow.includes(zoneOf(e.x, e.y))) &&
          (row === null || inSummaryRow(row, e)),
      )
      .map((e) => e.id);
    const zonesLabel = zonesNow.length ? (zonesNow.length === 1 ? "from 1 zone" : `from ${zonesNow.length} zones`) : "";
    const what = row ? PASS_SUMMARY_LABEL[row] : chips.length ? "" : "Passes";
    const label = [[what, zonesLabel].filter(Boolean).join(" "), ...chips].filter(Boolean).join(", ");
    selectPasses({ ids, label, source: mod });
  };
  const pickZone = (zone: number, add: boolean) => {
    undoApi().saveState();
    const has = zoneSel.includes(zone);
    const next = add ? (has ? zoneSel.filter((z) => z !== zone) : [...zoneSel, zone]) : has && zoneSel.length === 1 ? [] : [zone];
    setModState(mod, "_passZoneSelection", next);
    announce();
    forceRender();
  };
  const toggleZones = () => {
    undoApi().saveState();
    setModState(mod, "_passZones", !zonesOn);
    setModState(mod, "_passZoneSelection", []);
    announce();
    forceRender();
  };

  const setArea = (next: EventArea | null) => {
    setModState(mod, "_eventAreaSelection", next);
    forceRender();
  };

  const hasData = !!(spd && state);
  const subControls = (
    <EventSubControls
      mod={mod}
      spd={spd}
      state={state}
      onCompetitionChange={(compId) => {
        setModState(mod, "_eventMapCompetitionId", compId);
        // Deleted, not set to undefined — as the legacy does, so saveState captures no key.
        Reflect.deleteProperty(mod, "_eventMapSeason");
        forceRender();
      }}
      onSeasonChange={(seasonCode) => {
        setModState(mod, "_eventMapSeason", seasonCode);
        forceRender();
      }}
    >
      <button
        type="button"
        className="module-switch-btn event-view-toggle"
        style={hasData ? undefined : { display: "none" }}
        onClick={() => {
          setModState(mod, "_eventMapView", isTable ? "vis" : "table");
          forceRender();
        }}
      >
        {/* Names the view a click opens (Ben, 2026-10-31), not the one showing. */}
        {hasData ? (isTable ? "Chart" : "Table") : ""}
      </button>
    </EventSubControls>
  );

  if (!spd || !state || !active) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {/* Ben's one message (Q-012). No season: she has no events in any. */}
        <div className="stats-empty">{noDataMessage(cfg.emptyNoun, shortName, state?.seasonCode ?? emptySeason ?? null)}</div>
      </>
    );
  }

  if (!events.length) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {focus && seasonEvents.length ? (
          <>
            {focusChip}
            <div className="stats-empty">{focusEmptyText(shortName, cfg.pluralNoun, focus)}</div>
          </>
        ) : (
          <div className="stats-empty">{noDataMessage(cfg.emptyNoun, shortName, state.seasonCode)}</div>
        )}
      </>
    );
  }

  // Live: the header's ? card (explain.ts).
  const explainInput = {
    name: shortName,
    competition: state.comp.name,
    season: humanizeSeasonCode(state.seasonCode),
    total: events.length,
    shown: filtered.length,
    chips: eventChipsOf(active, defs),
    focus,
    colour: resolveColorMode(cfg.colorModes, getModState<string>(mod, cfg.colorStateKey) ?? cfg.defaultColor).label,
    table: isTable,
    zoomed: area !== null,
    zones: zonesOn ? zoneSel.length : null,
    teamSeason,
  };
  const explainer = LIVE && cfg.live && (
    <ModuleExplainer mod={mod} paragraphs={cfg.key === "shots" ? explainShotMap(explainInput) : explainPassMap(explainInput)} />
  );

  // Live Shot Map: the xG story over the whole season (Ben, 2026-10-18); a point narrows the map to
  // that match, again for the season. Not narrowed by the chips or a dragged-in focus: it's the
  // season's story, with the picked match ringed.
  const story =
    LIVE && cfg.live && cfg.key === "shots" && (playerId !== null || team) ? (
      <XgStory
        mod={mod}
        points={xgStory(ctx, seasonEvents)}
        selectedMatch={seasonEvents.find((e) => e.id === getModState<string | null>(mod, "_eventSelectedId"))?.match_id ?? null}
        picked={team ? teamMatch : focus?.kind === "match" && focus.matches.length === 1 ? focus.matches[0]!.matchId : null}
        onPick={(p) => {
          undoApi().saveState();
          if (team) {
            team.onPickMatch(teamMatch === p.matchId ? null : p.matchId);
            return;
          }
          if (playerId === null) return;
          const same = focus?.kind === "match" && focus.matches.length === 1 && focus.matches[0]!.matchId === p.matchId;
          setModuleFocus(
            mod,
            same
              ? null
              : {
                  kind: "match",
                  playerId,
                  competitionId: Number(state.compId),
                  seasonCode: state.seasonCode,
                  label: p.label === formatMatchDate(p.date) ? p.label : `${p.label} · ${formatMatchDate(p.date)}`,
                  matches: [{ matchId: p.matchId }],
                },
          );
          forceRender();
        }}
      />
    ) : null;

  const statsBar = (
    <EventStatsBar mod={mod} body={body} hidden={false}>
      {cfg.stats(zoned, events)}
    </EventStatsBar>
  );

  if (isTable) {
    const { Table } = cfg;
    return (
      <>
        {explainer}
        {subControls}
        {statsBar}
        <div className="event-map-wrap">
          {story}
          <EventFilterBar
            active={active}
            events={events}
            defs={defs}
            onChange={() => {
              bumpFilters();
              announce();
            }}
            // The pitch's corner Reset only exists in the pitch view — without this a zoomed
            // user switching to Table would have no way back.
            extra={
              <>
                {team?.controls}
                {team?.playersChip(events)}
                {focusChipInline}
                {linkedChip}
                {area && (
                  <button type="button" className="event-area-reset-inline-btn" onClick={() => setArea(null)}>
                    Reset view
                  </button>
                )}
              </>
            }
          />
          {zoned.length ? (
            <Table ctx={ctx} events={zoned} />
          ) : (
            <div className="stats-empty">{`No ${cfg.pluralNoun} match the current filters.`}</div>
          )}
        </div>
      </>
    );
  }

  // Seeded only in the pitch view: the legacy's wireColorByChip (which seeds it) never runs
  // in table view.
  if (!getModState<string>(mod, cfg.colorStateKey)) setModState(mod, cfg.colorStateKey, cfg.defaultColor);
  const colorMode = resolveColorMode(cfg.colorModes, getModState<string>(mod, cfg.colorStateKey));
  const { Pitch } = cfg;
  // The picked zones as a drag-out focus: the player, league + season, and any match / position
  // the map is narrowed to.
  const zoneCount = zoneSel.length === 1 ? "1 zone" : `${zoneSel.length} zones`;
  const zones: PitchZones | null = zonesOn
    ? {
        selected: zoneSel,
        onPick: pickZone,
        focus:
          zoneSel.length && playerId !== null
            ? {
                kind: "zone",
                playerId,
                competitionId: Number(state.compId),
                seasonCode: state.seasonCode,
                label: focus ? `${zoneCount} · ${focus.label}` : zoneCount,
                matches: focus ? focus.matches : [],
                zones: zoneSel,
              }
            : null,
      }
    : null;
  const zonesChip =
    LIVE && cfg.live && cfg.key === "passes" ? (
      // A pill like Colour's: click to switch the zone grid on / off.
      <div className={`filter-chip event-zones-chip${zonesOn ? " on" : ""}`}>
        <span className="chip-value" onClick={toggleZones} title={zonesOn ? "Hide the zones" : "Split the pitch into zones"}>
          Zones: {zonesOn ? "On" : "Off"}
        </span>
      </div>
    ) : null;

  // A blank pitch (no "no X match" text) when the chips exclude everything — the empty
  // pitch says it on its own.
  return (
    <>
      {explainer}
      {subControls}
      {statsBar}
      <div className="event-map-wrap">
        {story}
        <EventFilterBar
          active={active}
          events={events}
          defs={defs}
          onChange={() => {
            bumpFilters();
            announce();
          }}
          extra={
            <>
              {team?.controls}
              {team?.playersChip(events)}
              {zonesChip}
              {focusChipInline}
              {linkedChip}
            </>
          }
          colorBy={{
            options: cfg.colorModes.map((m) => m.label),
            current: colorMode.label,
            onPick: (label) => {
              const picked = cfg.colorModes.find((m) => m.label === label);
              if (picked) setModState(mod, cfg.colorStateKey, picked.key);
              forceRender();
            },
          }}
        />
        {team ? (
          // The players row above the side panel; it drags out as the list (Ben, 2026-11-01).
          <Pitch
            mod={mod}
            ctx={ctx}
            events={filtered}
            colorMode={colorMode}
            area={area}
            onArea={setArea}
            zones={zones}
            linkedIds={team.hoverIds}
            onOwnPick={announce}
            below={team.side(events)}
            zonesOnly={teamSeason && !hidden?.size && only === null}
          />
        ) : (
          <Pitch mod={mod} ctx={ctx} events={filtered} colorMode={colorMode} area={area} onArea={setArea} zones={zones} linkedIds={linked?.ids ?? null} onOwnPick={announce} />
        )}
      </div>
    </>
  );
}
